import React, { useState, useRef, useMemo } from 'react';
import { Box, Typography, Alert } from '@mui/material';
import { ClipboardDocumentIcon, KeyIcon } from '@heroicons/react/24/outline';
import ModalWrapper from '../ModalWrapper';
import ButtonComponent from '../customButton';
import { FormWrapper, FormSchemas, CommonFooter } from '../FormComponent';
import { createApiToken } from '../../utils/api/api_token_apis';
import { STATUS } from '../../utils/api/sharedfunction';
import { useSnackbar } from '../../utils/useSnackbar';
import {
  blockIntegerNumberInputKeyDown,
  blockIntegerNumberInputPaste,
  preventNumberInputWheel,
} from '../../utils/integerNumberInputHandlers';

const EXPIRES_IN_DAYS_MIN = 1;
const EXPIRES_IN_DAYS_MAX = 365;
const EXPIRES_IN_DAYS_DEFAULT = 90;

const FIELDS = [
  {
    name: 'name',
    type: 'text',
    label: 'Token name',
    placeholder: 'e.g. Cursor MCP, CI Pipeline',
    required: true,
    autoFocus: true,
  },
  {
    name: 'expiresInDays',
    type: 'number',
    label: 'Expires in (days)',
    placeholder: String(EXPIRES_IN_DAYS_DEFAULT),
    helperText: `Enter a whole number from ${EXPIRES_IN_DAYS_MIN} to ${EXPIRES_IN_DAYS_MAX}`,
    required: true,
    validation: { min: EXPIRES_IN_DAYS_MIN, max: EXPIRES_IN_DAYS_MAX },
    min: EXPIRES_IN_DAYS_MIN,
    max: EXPIRES_IN_DAYS_MAX,
    step: 1,
    onKeyDown: blockIntegerNumberInputKeyDown,
    onPaste: blockIntegerNumberInputPaste,
    onWheel: preventNumberInputWheel,
  },
];

const CreateTokenModal = ({ open, onClose, onSuccess, existingTokenNames = [], slackLinkToken }) => {
  const formRef = useRef(null);
  const [modalError, setModalError] = useState('');
  const { showSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);
  const [createdToken, setCreatedToken] = useState(null);

  const defaultName = slackLinkToken ? 'Slack Link' : '';

  const resetForm = () => {
    setCreatedToken(null);
    setModalError('');
    if (formRef.current) formRef.current.resetForm({ name: defaultName, expiresInDays: EXPIRES_IN_DAYS_DEFAULT });
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleFormSubmit = () => {
    setModalError('');
    if (formRef.current) formRef.current.submit();
  };

  const handleConfirmCreate = async (data, { setError } = {}) => {
    setLoading(true);
    setModalError('');
    try {
      const response = await createApiToken({
        name: data.name?.trim(),
        expiresInDays: Number(data.expiresInDays) || EXPIRES_IN_DAYS_DEFAULT,
        slackLinkToken,
      });
      if (response.status === STATUS.SUCCESS) {
        setCreatedToken(response.data);
        showSnackbar(response.message || 'Token created successfully', 'success');
      } else {
        const errMsg = response.error || response.message || 'Failed to create token';
        if (response.code === 'DUPLICATE_TOKEN_NAME' && setError) {
          setError('name', { type: 'server', message: errMsg });
        }
        setModalError(errMsg);
        showSnackbar(errMsg, 'error');
      }
    } catch (err) {
      const errMsg = err.message || 'An error occurred';
      setModalError(errMsg);
      showSnackbar(errMsg, 'error');
    } finally {
      setLoading(false);
    }
  };

  const tokenFormSchema = useMemo(() => FormSchemas.apiTokenCreation(existingTokenNames), [existingTokenNames]);

  const handleCopyToken = () => {
    if (createdToken?.token) {
      navigator.clipboard.writeText(createdToken.token);
      showSnackbar('Token copied to clipboard', 'success');
    }
  };

  const handleDone = () => {
    if (onSuccess) onSuccess();
    handleClose();
  };

  const footer = createdToken ? (
    <CommonFooter
      buttons={[
        {
          label: 'Done',
          variant: 'primary',
          className: 'white bg-blue600',
          onClick: handleDone,
        },
      ]}
      align="right"
    />
  ) : (
    <CommonFooter
      buttons={[
        {
          label: 'Cancel',
          variant: 'outline',
          className: 'primary',
          onClick: handleClose,
          disabled: loading,
        },
        {
          label: loading ? 'Creating...' : 'Generate Token',
          variant: 'primary',
          className: 'white bg-blue600',
          onClick: handleFormSubmit,
          disabled: loading,
          loading,
        },
      ]}
      align="right"
    />
  );

  return (
    <ModalWrapper
      isOpen={open}
      onClose={handleClose}
      title={createdToken ? 'Token Created' : slackLinkToken ? 'Link Slack Account' : 'Generate New API Token'}
      size="small"
      footer={footer}
      closeOnBackdrop={false}
      closeOnEsc={!createdToken}
      showCloseButton={!createdToken}
    >
      {createdToken ? (
        <Box sx={{ px: 2, pb: 2 }}>
          <Alert severity="warning" sx={{ mb: 2 }} icon={false}>
            <Typography variant="body2" fontWeight={500} sx={{ mb: 0.5 }}>
              Copy this token now
            </Typography>
            <Typography variant="body2" color="text.secondary">
              You won&apos;t be able to see it again after closing this dialog.
            </Typography>
          </Alert>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 1.5,
              p: 2,
              bgcolor: 'grey.50',
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <KeyIcon
              style={{
                width: 20,
                height: 20,
                color: 'var(--mui-palette-text-secondary)',
                flexShrink: 0,
                marginTop: 2,
              }}
            />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                component="code"
                sx={{
                  display: 'block',
                  fontSize: '0.8125rem',
                  fontFamily: 'monospace',
                  wordBreak: 'break-all',
                  lineHeight: 1.5,
                }}
              >
                {createdToken.token}
              </Typography>
            </Box>
            <ButtonComponent
              variant="secondary"
              ctaText="Copy"
              size="small"
              onClick={handleCopyToken}
              startIcon={<ClipboardDocumentIcon style={{ width: 16, height: 16 }} />}
              className="slate600"
            />
          </Box>
        </Box>
      ) : (
        <Box sx={{ px: 2, pb: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {slackLinkToken
              ? 'Create a token to link your Slack account. Slack will use it to act as you when you ask QA-Path questions there.'
              : 'Create a token to authenticate MCP clients or other integrations. Give it a descriptive name.'}
          </Typography>
          {modalError && (
            <Typography variant="body2" color="error" sx={{ mb: 2 }}>
              {modalError}
            </Typography>
          )}
          <FormWrapper
            ref={formRef}
            schema={tokenFormSchema}
            fields={FIELDS}
            onSubmit={handleConfirmCreate}
            defaultValues={{
              name: defaultName,
              expiresInDays: EXPIRES_IN_DAYS_DEFAULT,
            }}
          />
        </Box>
      )}
    </ModalWrapper>
  );
};

export default CreateTokenModal;
